import { useState, useCallback } from 'react';
import { useSupabase } from './useSupabase';
import {
  getMarketProjections,
  estimateAnnualRevenue,
  MarketProjections,
} from '../services/marketProjectionService';

interface UseMarketProjectionsReturn {
  projections: MarketProjections | null;
  estimatedRevenue: { estimatedAnnualRevenue: number; estimatedCStoreRevenue: number } | null;
  isLoading: boolean;
  error: string | null;
  fetchProjections: (stationObjectId: number) => Promise<void>;
}

export function useMarketProjections(): UseMarketProjectionsReturn {
  const { supabase } = useSupabase();
  const [projections, setProjections] = useState<MarketProjections | null>(null);
  const [estimatedRevenue, setEstimatedRevenue] = useState<{ estimatedAnnualRevenue: number; estimatedCStoreRevenue: number } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjections = useCallback(async (stationObjectId: number) => {
    if (!supabase) {
      setError('Supabase client is not available.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setProjections(null);
    setEstimatedRevenue(null);

    try {
      const result = await getMarketProjections(supabase.client, stationObjectId);

      if (result.success && result.data) {
        setProjections(result.data);
        const revenue = estimateAnnualRevenue(result.data);
        setEstimatedRevenue(revenue);
      } else {
        setError(result.error || 'Failed to fetch market projections.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  return { projections, estimatedRevenue, isLoading, error, fetchProjections };
}
